import { ErrorModal, useUserContext } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { SectionFamily } from "../models/SectionFamily";
import { getSectionFamilies } from "../repositories/SectionFamilyRepository";

type Props = {
	label: string;
	value: number | null;
	fieldError?: FieldError;
	onChange?: Function;
	disabled?: boolean;
	required?: boolean;
};

const useSectionFamilies = (
	sectionFamilyId?: number,
	onError?: (error: Error) => void,
) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	return useQuery<SectionFamily[], Error>({
		queryKey: ["sectionFamilies", sectionFamilyId, defaultAreaId],
		queryFn: ({ signal }) =>
			getSectionFamilies({ sectionFamilyId, defaultAreaId }, signal),
		onError: onError,
	});
};

export const SectionFamilySelector = ({
	label,
	value,
	fieldError,
	onChange = () => {},
	disabled = false,
	required = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: sectionFamilies, isLoading } = useSectionFamilies(
		undefined,
		(error) => setError(error.message),
	);

	const selectedValue =
		sectionFamilies?.find((section) => section.SectionFamilyId === value) ||
		null;

	return (
		<>
			<Autocomplete
				clearOnEscape
				autoHighlight
				options={sectionFamilies || []}
				getOptionLabel={(option) => option.SectionFamilyName}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						inputProps={{
							...params.inputProps,
							"data-test-id": "section-family-selector",
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedValue}
				onChange={(event, newValue) => {
					onChange(newValue?.SectionFamilyId);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading sections"
			/>
		</>
	);
};
